/**
 * Customer Preferences Component
 *
 * Form for editing customer-specific preferences including:
 * - Activity interests
 * - Skill level
 * - Travel preferences
 *
 * Epic: MVP 1.21.4 - Customer Dashboard
 * Story: US-1.21.4-17: Preferences Setup (2 SP)
 *
 * Props:
 * - preferences: object - Current customer preferences
 * - onUpdate: function - Callback when preferences are updated successfully
 */

import React, { useState } from 'react';
import apiRequest from '../../services/api';
import {
  Compass,
  TrendingUp,
  MapPin,
  Save,
  CheckCircle,
  AlertCircle,
  Users
} from 'lucide-react';

export default function CustomerPreferences({ preferences, onUpdate }) {
  // Form state
  const [formData, setFormData] = useState({
    activity_interests: preferences?.activity_interests || [],
    skill_level: preferences?.skill_level || 'beginner',
    willing_to_travel_distance: preferences?.willing_to_travel_distance || 50,
    group_size_preference: preferences?.group_size_preference || 'any',
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Activity options
  const activityOptions = [
    { value: 'fishing', label: 'Fishing' },
    { value: 'hunting', label: 'Hunting' },
    { value: 'hiking', label: 'Hiking' },
    { value: 'camping', label: 'Camping' },
    { value: 'kayaking', label: 'Kayaking' },
    { value: 'climbing', label: 'Rock Climbing' },
    { value: 'wildlife', label: 'Wildlife Viewing' },
    { value: 'photography', label: 'Nature Photography' },
    { value: 'backpacking', label: 'Backpacking' },
  ];

  // Skill level options
  const skillLevels = [
    { value: 'beginner', label: 'Beginner', description: 'New to outdoor activities' },
    { value: 'intermediate', label: 'Intermediate', description: 'Some experience' },
    { value: 'advanced', label: 'Advanced', description: 'Experienced outdoors person' },
    { value: 'expert', label: 'Expert', description: 'Highly skilled' },
  ];

  // Group size options
  const groupSizes = [
    { value: 'solo', label: 'Solo', description: 'I prefer private experiences' },
    { value: 'small', label: 'Small Group', description: '2-5 people' },
    { value: 'medium', label: 'Medium Group', description: '6-10 people' },
    { value: 'any', label: 'Any Size', description: 'No preference' },
  ];

  /**
   * Handle activity interest toggle
   */
  const handleActivityToggle = (activity) => {
    setFormData(prev => {
      const interests = prev.activity_interests.includes(activity)
        ? prev.activity_interests.filter(a => a !== activity)
        : [...prev.activity_interests, activity];

      return { ...prev, activity_interests: interests };
    });
    setHasChanges(true);
    setSuccess(false);
    setError(null);
  };

  /**
   * Handle input changes
   */
  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setHasChanges(true);
    setSuccess(false);
    setError(null);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (_e) => {
    _e.preventDefault();

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response = await apiRequest('/api/v1/customer/preferences', {
        method: 'PUT',
        body: JSON.stringify(formData)
      });

      if (response.success) {
        setSuccess(true);
        setHasChanges(false);
        onUpdate(response.data.data);

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error updating preferences:', err);
      setError(err.message || 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">Your Preferences</h3>
      <p className="text-sm text-gray-600 mb-6">
        Help us personalize your experience by sharing your interests and preferences.
      </p>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Activity Interests */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Compass className="w-4 h-4 inline mr-2" />
            Activity Interests
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Select all activities you&apos;re interested in
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {activityOptions.map((activity) => {
              const isSelected = formData.activity_interests.includes(activity.value);
              return (
                <button
                  key={activity.value}
                  type="button"
                  onClick={() => handleActivityToggle(activity.value)}
                  className={`
                    px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors
                    ${isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }
                  `}
                >
                  {activity.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Skill Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <TrendingUp className="w-4 h-4 inline mr-2" />
            Skill Level
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Your general outdoor experience level
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {skillLevels.map((level) => {
              const isSelected = formData.skill_level === level.value;
              return (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => handleChange('skill_level', level.value)}
                  className={`
                    px-4 py-3 rounded-lg border-2 text-left transition-colors
                    ${isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                    }
                  `}
                >
                  <div className="font-medium text-sm text-gray-900">{level.label}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{level.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Travel Distance */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <MapPin className="w-4 h-4 inline mr-2" />
            Willing to Travel
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Maximum distance you&apos;re willing to travel for an experience
          </p>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="10"
              max="500"
              step="10"
              value={formData.willing_to_travel_distance}
              onChange={(e) => handleChange('willing_to_travel_distance', parseInt(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
            <span className="text-sm font-medium text-gray-900 min-w-[80px] text-right">
              {formData.willing_to_travel_distance} miles
            </span>
          </div>
        </div>

        {/* Group Size Preference */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Users className="w-4 h-4 inline mr-2" />
            Group Size Preference
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Your preferred group size for experiences
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {groupSizes.map((size) => {
              const isSelected = formData.group_size_preference === size.value;
              return (
                <button
                  key={size.value}
                  type="button"
                  onClick={() => handleChange('group_size_preference', size.value)}
                  className={`
                    px-4 py-3 rounded-lg border-2 text-left transition-colors
                    ${isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                    }
                  `}
                >
                  <div className="font-medium text-sm text-gray-900">{size.label}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{size.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div
            role="status"
            aria-live="polite"
            className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800 font-medium">
              Preferences updated successfully!
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div
            role="alert"
            aria-live="assertive"
            className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!hasChanges || loading}
            className={`
              flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors
              ${!hasChanges || loading
                ? 'bg-gray-300 text-gray-700 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
              }
            `}
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </form>
    </div>
  );
}
